import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideBlockList,
} from "../GuideBlocks.js";

const CognitiveBiasesSection = ({ heading, accentIndex = 2, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="cognitive-biases"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      Your mind is not built for perfect accuracy. It is built for speed. That trade-off shows up as{" "}
      <strong>cognitive biases</strong> — mental shortcuts that help you decide fast, often at the
      cost of deciding well.
    </GuideLead>
    <p className="guide-panel__p">
      You are not immune because you are sharp. Sharp people often have more sophisticated ways of
      justifying their biases — that is it.
    </p>
    <GuideSectionLabel>Biases that show up most in everyday decisions</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="thinking"
        title="Confirmation bias"
        description="Noticing evidence that supports what you already believe, and skimming past what does not."
      />
      <GuideCard
        iconKey="awareness"
        title="Recency bias"
        description="Overweighting whatever happened most recently, even if it is not representative."
      />
      <GuideCard
        iconKey="energy"
        title="Sunk cost fallacy"
        description="Continuing something because of what you have already invested, not because it is still the right choice."
      />
      <GuideCard
        iconKey="clarity"
        title="Anchoring"
        description="Letting the first number or idea you hear shape everything that follows, even when it is arbitrary."
      />
    </GuideCardGrid>
    <p className="guide-panel__p">
      <strong>Negativity bias</strong> — giving more weight to one critical comment than ten pieces
      of useful feedback — belongs on the same list. None of these are character flaws. They are
      default settings.
    </p>
    <GuideSectionLabel>Where bias enters EIER</GuideSectionLabel>
    <p className="guide-panel__p">
      Cognitive bias does not distort the Event — it distorts the{" "}
      <strong>Interpretation</strong> step of EIER (Event → Interpretation → Evidence → Response).
      The event is neutral; your mind adds a story almost instantly, and bias shapes how that story
      gets written.
    </p>
    <GuideCallout>
      Interpretation often feels like fact. That is what makes bias hard to catch — you think you
      are observing when you are interpreting.
    </GuideCallout>
    <GuideSectionLabel>Catching bias before it becomes a decision</GuideSectionLabel>
    <GuideBlockList
      items={[
        'Ask "what would change my mind?" If nothing would, you are likely inside a confirmation bias loop.',
        'Separate the decision from the sunk cost. Ask: "If I were starting fresh today, would I still choose this?"',
        "Widen your time window. Recency bias fades when you deliberately compare this week to the last three months.",
        'Write the Interpretation down before responding. Seeing "I think this means ___" in writing makes it easier to question than when it runs silently.',
      ]}
    />
    <GuideCallout variant="amber">
      You cannot delete cognitive bias. You can build a habit of noticing it at the Interpretation
      step, before it drives a Response you did not actually choose.
    </GuideCallout>
  </GuidePanel>
);

export default CognitiveBiasesSection;
